import { soundService } from '../services/soundService';

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import {
  Sparkles, ArrowLeft, Loader2, Trophy, ChevronDown,
  Repeat, Mic, Send, CheckCircle2, RotateCcw, Edit3, Trash2,
  ChevronLeft, ChevronRight, Globe, X, Bookmark, HelpCircle,
  Volume2, Square
} from 'lucide-react';
import { getWritingExercises } from '../services/exercises';
import { exerciseProgressService } from '../services/exerciseProgressService';
import { getLocalISOString } from '../utils/timezoneUtils';
import { textToSpeechService } from '../services/textToSpeechService';
import { groqService } from '../services/groqService';
import { Brain, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { notesDeckManager } from '../services/notesDeckManager';
import { supabase } from '../services/supabaseClient';
import { dbManager } from '../services/db_manager';
import { useAuth } from '../contexts/AuthContext';
import { useStreakCounter } from '../hooks/useStreakCounter';
import { LANG_LABEL_MAP as GLOBAL_LANG_LABEL_MAP, getLanguageName, getLanguageCode } from '../services/languageConstants';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { statsService } from '../services/statsService';
import { activeStudyTracker } from '../services/activeStudyTrackerService';
import { AppBackButton, goBackOr, canGoBack } from '../components/AppBackButton';
import { useTranslation } from 'react-i18next';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';
import { tourService } from '../services/tourService';
import { streakService } from '../services/streakService';
import { translationService } from '../services/translationService';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { AnimatedIcon } from '../components/AnimatedIcon';
import T from '../components/T';
import i18nInstance from '../i18n';


// Firebase removed - Using R2

interface Language {
  id: string;
  name: string;
  flag: string;
  folder: string;
}

const TRANSLATING_LABELS: Record<string, string> = {
  pt: 'Traduzindo...',
  en: 'Translating...',
  es: 'Traduciendo...',
  fr: 'Traduction...',
  de: 'Wird übersetzt...',
  it: 'Traducendo...',
  ru: 'Перевод...',
  zh: '正在翻译...',
  ja: '翻訳中...',
  nl: 'Vertalen...'
};

const LANGUAGES: Language[] = [
  { id: 'en-US', name: 'Inglês', flag: '🇺🇸', folder: 'ingles' },
  { id: 'fr-FR', name: 'Francês', flag: '🇫🇷', folder: 'frances' },
  { id: 'de-DE', name: 'Alemão', flag: '🇩🇪', folder: 'alemao' },
  { id: 'pt-BR', name: 'Português', flag: '🇧🇷', folder: 'portugues' },
  { id: 'it-IT', name: 'Italiano', flag: '🇮🇹', folder: 'italiano' },
  { id: 'es-ES', name: 'Espanhol', flag: '🇪🇸', folder: 'espanhol' },
  { id: 'nl-NL', name: 'Holandês', flag: '🇳🇱', folder: 'holandes' },
  { id: 'ja-JP', name: 'Japonês', flag: '🇯🇵', folder: 'japones' },
  { id: 'ru-RU', name: 'Russo', flag: '🇷🇺', folder: 'russo' },
  { id: 'zh-CN', name: 'Chinês', flag: '🇨🇳', folder: 'chines' },
  { id: 'hu-HU', name: 'Húngaro', flag: '🇭🇺', folder: 'hungaro' }
];

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const WRITING_AUDIO_LANG_MAP: Record<string, string> = {
  en: 'english',
  fr: 'french',
  de: 'german',
  it: 'italian',
  pt: 'portuguese',
  ru: 'russian',
  es: 'spanish',
  'en-us': 'english',
  'fr-fr': 'french',
  'de-de': 'german',
  'it-it': 'italian',
  'pt-br': 'portuguese',
  'ru-ru': 'russian',
  'es-es': 'spanish',
  ingles: 'english',
  frances: 'french',
  alemao: 'german',
  italiano: 'italian',
  portugues: 'portuguese',
  russo: 'russian',
  espanhol: 'spanish'
};

const WritingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const tx = (key: string, fallback: string) => t(`writing_page.${key}`, fallback);
  const translatingLabel = TRANSLATING_LABELS[(i18n.language || 'pt').split('-')[0].toLowerCase()] || TRANSLATING_LABELS.pt;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isGuide = location.pathname.includes('/guide') || searchParams.get('from') === 'guide';
  const { user } = useAuth();
  const { profile, refreshProfile } = useData();
  const { targetLanguage: globalTargetLang } = useTheme();
  const [view, setView] = useState<'practice' | 'notesPractice'>('practice');
  useStreakCounter(true);

  const storyAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isStoryAudioPlaying, setIsStoryAudioPlaying] = useState(false);
  const [isStoryAudioLoading, setIsStoryAudioLoading] = useState(false);

  const stopStoryAudio = () => {
    if (storyAudioRef.current) {
      try {
        storyAudioRef.current.pause();
        storyAudioRef.current.currentTime = 0;
        storyAudioRef.current.onended = null;
        storyAudioRef.current.onerror = null;
      } catch (e) {}
      storyAudioRef.current = null;
    }
    textToSpeechService.stop();
    setIsStoryAudioPlaying(false);
    setIsStoryAudioLoading(false);
  };

  useEffect(() => {
    return () => {
      stopStoryAudio();
    };
  }, []);

  const [exerciseMode, setExerciseMode] = useState<'traducao' | 'producao'>(() => {
    return (localStorage.getItem('libritui_writing_exerciseMode') as 'traducao' | 'producao') || 'traducao';
  });

  const handleModeChange = (mode: 'traducao' | 'producao') => {
    setExerciseMode(mode);
    localStorage.setItem('libritui_writing_exerciseMode', mode);
  };
  
  const { langCode: routeLang, level: routeLevel } = useParams<{ langCode?: string; level?: string }>();

  // Set speakingLang dynamically based on targetLanguage
  const [speakingLang, setSpeakingLang] = useState<Language>(() => {
    return LANGUAGES.find(l => l.id.split('-')[0] === globalTargetLang) || LANGUAGES.find(l => l.id === 'pt-BR')!;
  });
  
  const [learningLang, setLearningLang] = useState<Language>(() => {
    const raw = routeLang || searchParams.get('lang');
    if (raw) {
      const clean = getLanguageCode(raw);
      const match = LANGUAGES.find(l => l.id.split('-')[0].toLowerCase() === clean.toLowerCase());
      if (match) return match;
    }
    return LANGUAGES[0];
  });
  const [activeLevel, setActiveLevel] = useState<string>(() => {
    const rawLvl = routeLevel || searchParams.get('level');
    return rawLvl ? rawLvl.toUpperCase() : 'A1';
  });

  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);

  const handleLanguageChange = (newLang: Language) => {
    stopStoryAudio();
    setLearningLang(newLang);
    setCurrentStoryIndex(0);
    setUserInput('');
    setFeedback(null);
    const langCode = newLang.id.split('-')[0];
    if (isGuide) {
      navigate(`/writing/guide/${langCode}/${activeLevel.toLowerCase()}?from=guide`, { replace: true });
    } else {
      navigate(`/writing/${langCode}/${activeLevel.toLowerCase()}`, { replace: true });
    }
  };

  const handleLevelChange = (newLevel: string) => {
    stopStoryAudio();
    setActiveLevel(newLevel);
    setCurrentStoryIndex(0);
    setUserInput('');
    setFeedback(null);
    const langCode = learningLang.id.split('-')[0];
    if (isGuide) {
      navigate(`/writing/guide/${langCode}/${newLevel.toLowerCase()}?from=guide`, { replace: true });
    } else {
      navigate(`/writing/${langCode}/${newLevel.toLowerCase()}`, { replace: true });
    }
  };

  useEffect(() => {
    const rawLang = routeLang || searchParams.get('lang');
    const rawLevel = routeLevel || searchParams.get('level');
    if (rawLang) {
      const clean = getLanguageCode(rawLang);
      const match = LANGUAGES.find(l => l.id.split('-')[0].toLowerCase() === clean.toLowerCase());
      if (match) setLearningLang(match);
    }
    if (rawLevel) {
      setActiveLevel(rawLevel.toUpperCase());
    }
  }, [routeLang, routeLevel, searchParams]);

  const [stories, setStories] = useState<any[]>([]);
  const [selectedStory, setSelectedStory] = useState<any>(null);
  const [translatedStoryContent, setTranslatedStoryContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (stories.length > 0) {
      const idx = currentStoryIndex < stories.length ? currentStoryIndex : 0;
      setSelectedStory(stories[idx]);
    } else {
      setSelectedStory(null);
    }
  }, [stories, currentStoryIndex]);

  const handlePrevStory = () => {
    stopStoryAudio();
    if (currentStoryIndex > 0) {
      const newIdx = currentStoryIndex - 1;
      setCurrentStoryIndex(newIdx);
      setSelectedStory(stories[newIdx]);
      setUserInput('');
      setFeedback(null);
    }
  };

  const handleNextStory = () => {
    stopStoryAudio();
    if (currentStoryIndex < stories.length - 1) {
      const newIdx = currentStoryIndex + 1;
      setCurrentStoryIndex(newIdx);
      setSelectedStory(stories[newIdx]);
      setUserInput('');
      setFeedback(null);
    }
  };

  const handleToggleStoryAudio = async () => {
    if (isStoryAudioPlaying) {
      stopStoryAudio();
      return;
    }

    if (!selectedStory) return;

    const storyText = selectedStory.content || selectedStory.texto || '';
    if (!storyText.trim()) return;

    const langKey = (learningLang?.id || 'en').split('-')[0].toLowerCase();
    const folder = WRITING_AUDIO_LANG_MAP[langKey] || WRITING_AUDIO_LANG_MAP[learningLang?.folder] || null;
    const level = (activeLevel || 'A1').toUpperCase();
    const storyId = selectedStory?.id !== undefined ? selectedStory.id : currentStoryIndex;
    const audioFilename = `historia-${storyId}.mp3`;

    setIsStoryAudioLoading(true);

    const tryPlayAudioUrl = (url: string): Promise<boolean> => {
      return new Promise((resolve) => {
        try {
          const audio = new Audio(url);
          storyAudioRef.current = audio;

          audio.onended = () => {
            storyAudioRef.current = null;
            setIsStoryAudioPlaying(false);
            setIsStoryAudioLoading(false);
          };

          audio.onerror = () => {
            storyAudioRef.current = null;
            resolve(false);
          };

          audio.play()
            .then(() => {
              setIsStoryAudioPlaying(true);
              setIsStoryAudioLoading(false);
              resolve(true);
            })
            .catch(() => {
              storyAudioRef.current = null;
              resolve(false);
            });
        } catch {
          resolve(false);
        }
      });
    };

    if (selectedStory.audioUrl) {
      const playedDirect = await tryPlayAudioUrl(selectedStory.audioUrl);
      if (playedDirect) return;
    }

    if (folder) {
      const audioFilename = selectedStory.audio_file || `historia-${storyId}.mp3`;
      // Primary direct path on R2 (validated 200 OK)
      const primaryUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/writing-audio/${folder}/${level}/${audioFilename}`;
      const playedPrimary = await tryPlayAudioUrl(primaryUrl);
      if (playedPrimary) return;

      // Fallback path with biblioteca-backup/ prefix
      const fallbackR2Url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-backup/writing-audio/${folder}/${level}/${audioFilename}`;
      const playedFallbackR2 = await tryPlayAudioUrl(fallbackR2Url);
      if (playedFallbackR2) return;
    }

    // Fallback to textToSpeechService
    setIsStoryAudioLoading(false);
    setIsStoryAudioPlaying(true);
    try {
      await textToSpeechService.speak(storyText, learningLang.id);
    } catch (e) {
      console.warn('[WritingPage] Fallback TTS error:', e);
    } finally {
      setIsStoryAudioPlaying(false);
    }
  };

  const handleGoBack = () => {
    if (isGuide) {
      navigate('/');
      return;
    }
    if (tourService.isActive() && tourService.getPhase() === 'GOTO_VOZ_ATIVA') {
      navigate(-1);
      return;
    }
    goBackOr(navigate, '/study-hub');
  };
  const [userInput, setUserInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [writingDecks, setWritingDecks] = useState<{ lang: string; name: string; cards: any[] }[]>([]);
  const [selectedNotesDeck, setSelectedNotesDeck] = useState<{ lang: string; name: string; cards: any[] } | null>(null);
  const [noteCardIndex, setNoteCardIndex] = useState(0);

  const [deckToDelete, setDeckToDelete] = useState<string | null>(null);

  const [editingDeck, setEditingDeck] = useState<{ lang: string; name: string; cards: any[] } | null>(null);
  const [selectedCardsForDeletion, setSelectedCardsForDeletion] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [editingVisibleCount, setEditingVisibleCount] = useState(20);
  const [showConfirmBatchDelete, setShowConfirmBatchDelete] = useState(false);

  const [isSavingCorrection, setIsSavingCorrection] = useState(false);
  const [savedCorrectionSuccess, setSavedCorrectionSuccess] = useState(false);
  const [showSavedCorrectionModal, setShowSavedCorrectionModal] = useState(false);
  const [savedCorrections, setSavedCorrections] = useState<any[]>([]);

  // Tour States
  const [showModeTour, setShowModeTour] = useState(false);

  const unifiedDict = useUnifiedDictionary();

  const handleWordClick = async (e: React.MouseEvent<HTMLSpanElement>, word: string, forceSrcLang?: string) => {
    e.stopPropagation();
    const cleanWord = word.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?\"']/g, "").trim();
    if (!cleanWord) return;

    const userNative = (i18n.language || 'pt').split('-')[0].toLowerCase();
    const targetLearn = (learningLang ? learningLang.id : 'en').split('-')[0].toLowerCase();

    let sourceL: string;
    let targetL: string;

    if (forceSrcLang) {
      sourceL = forceSrcLang.split('-')[0].toLowerCase();
      targetL = (sourceL === userNative) ? targetLearn : userNative;
    } else if (exerciseMode === 'producao') {
      sourceL = userNative;
      targetL = targetLearn;
    } else {
      sourceL = targetLearn;
      targetL = userNative;
    }

    if (sourceL === targetL) {
      targetL = targetLearn !== sourceL ? targetLearn : (sourceL === 'pt' ? 'en' : 'pt');
    }

    unifiedDict.handleWordClick(e, cleanWord, sourceL, targetL);
  };

  const renderClickableText = (text: string, forceSrcLang?: string) => {
    const parts = text.split(/(\s+)/);
    return parts.map((part, idx) => {
      const hasLetters = /[a-zA-ZÀ-ÿ\w]/.test(part);
      if (hasLetters) {
        return (
          <span 
            key={idx}
            onClick={(e) => handleWordClick(e, part, forceSrcLang)}
            className="hover:text-primary hover:underline cursor-pointer transition-colors relative inline-block text-lg font-medium dark:text-ink"
          >
            {part}
          </span>
        );
      }
      return <span key={idx} className="text-lg font-medium text-ink dark:text-slate-200">{part}</span>;
    });
  };

  // Active study tracker initialization & unmount flush for /writing
  useEffect(() => {
    const rawLang = (view === 'notesPractice' && selectedNotesDeck)
      ? selectedNotesDeck.lang
      : (learningLang ? learningLang.id : profile?.learning_language);
    const clean = rawLang ? rawLang.split('-')[0].toLowerCase() : null;

    if (user?.id && clean && clean !== 'all') {
      activeStudyTracker.setUserContext(user.id, clean);
    }
    return () => {
      activeStudyTracker.flush();
    };
  }, [user?.id, view, selectedNotesDeck?.lang, learningLang?.id, profile?.learning_language]);

  const [showTypeTour, setShowTypeTour] = useState(false);
  const [expandedSavedDeck, setExpandedSavedDeck] = useState<string | null>(null);

  const [correctionCount, setCorrectionCount] = useState<number>(0);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const FREE_LIMIT = 5;
  const isPro = profile && (profile.plan_type === 'pro' || profile.subscription_tier === 'pro');

  const handleEditDeck = (e: React.MouseEvent, deck: { lang: string; name: string; cards: any[] }) => {
    e.stopPropagation();
    setEditingDeck(deck);
    setIsSelectionMode(false);
    setSelectedCardsForDeletion(new Set());
    setEditingVisibleCount(20);
  };

  const handleScrollEditView = (e: React.UIEvent<HTMLDivElement>) => {
    const bottom = e.currentTarget.scrollHeight - e.currentTarget.scrollTop <= e.currentTarget.clientHeight + 100;
    if (bottom && editingDeck && editingVisibleCount < editingDeck.cards.length) {
      setEditingVisibleCount(prev => prev + 20);
    }
  };

  const handleBatchDelete = () => {
    const idsToDelete = Array.from(selectedCardsForDeletion);
    if (idsToDelete.length === 0) return;

    import('../services/flashcardManager').then(({ flashcardManager }) => {
       flashcardManager.enqueueDeletion(idsToDelete);
    });

    setWritingDecks(prev => prev.map(d => {
       if (d.lang === editingDeck?.lang) {
          return { ...d, cards: d.cards.filter(c => !idsToDelete.includes(String(c.id))) };
       }
       return d;
    }));
    
    if (editingDeck) {
       setEditingDeck(prev => prev ? { ...prev, cards: prev.cards.filter(c => !idsToDelete.includes(String(c.id))) } : null);
    }
    
    setShowConfirmBatchDelete(false);
    setIsSelectionMode(false);
    setSelectedCardsForDeletion(new Set());
  };

  // Using GLOBAL_LANG_LABEL_MAP from languageConstants
  const LANG_LABEL_MAP = GLOBAL_LANG_LABEL_MAP;


  const [activeTab, setActiveTab] = useState<'geral' | 'focado'>('geral');

  // Deck metadata (names and favorites)
  const [deckMeta, setDeckMeta] = useState<Record<string, { name?: string, isFavorite?: boolean }>>(() => {
    const saved = localStorage.getItem('libritui_writing_deck_meta');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('libritui_writing_deck_meta', JSON.stringify(deckMeta));
  }, [deckMeta]);

  const toggleFavorite = (lang: string) => {
    setDeckMeta(prev => ({
      ...prev,
      [lang]: { ...prev[lang], isFavorite: !prev[lang]?.isFavorite }
    }));
  };

  const renameDeck = (lang: string) => {
    const currentName = deckMeta[lang]?.name || LANG_LABEL_MAP[lang] || lang;
    const newName = prompt('Editar nome do baralho:', currentName);
    if (newName && newName.trim()) {
      setDeckMeta(prev => ({
        ...prev,
        [lang]: { ...prev[lang], name: newName.trim() }
      }));
    }
  };

  const deleteDeck = async (lang: string) => {
    notesDeckManager.deleteWritingDeck(lang); // Background
    setWritingDecks(prev => prev.filter(d => d.lang !== lang));
    setDeckToDelete(null);
    alert("Excluído com sucesso!");
  };



  // Load writing notes decks — refreshes whenever Brain icon saves new data
  const loadWritingDecks = async () => {
    try {
      // 1. Load Local Notes
      const localNotes = notesDeckManager.getWritingNotes();

      // 2. Load Cloud Notes
      let cloudNotes: any[] = [];
      if (user && !user.isGuest) {
        const { data, error } = await dbManager.from('user_writing', user.id).select();
        if (data) {
          data.sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        }

        if (error) {
          console.error("Cloud fetch error (Writing):", error);
        } else if (data) {
          cloudNotes = data;
        }
      }

      // 3. Merge & Deduplicate
      const mergedMap = new Map<string, any>();

      localNotes.forEach(n => {
        const key = `${n.original.trim().toLowerCase()}|${n.langCode}`;
        mergedMap.set(key, { ...n, source: 'local' });
      });

      cloudNotes.forEach(n => {
        const key = `${n.original.trim().toLowerCase()}|${n.lang_code || n.language}`;
        if (!mergedMap.has(key)) {
          mergedMap.set(key, {
            id: n.id,
            original: n.original,
            translation: n.translation,
            langCode: n.lang_code || n.language,
            category: n.category,
            source: 'cloud'
          });
        }
      });

      const mergedNotes = Array.from(mergedMap.values());
      const grouped = notesDeckManager.groupByLang(mergedNotes);

      const decks = Object.entries(grouped).map(([langOrCat, cards]) => ({
        lang: langOrCat,
        name: langOrCat.length > 5 ? langOrCat : (LANG_LABEL_MAP[langOrCat] || langOrCat),
        cards: cards.map((c: any) => ({
          ...c,
          lang: c.langCode || langOrCat // Normalize key for consistency
        }))
      }));
      setWritingDecks(decks);
    } catch (err) {
      console.error("Falha ao carregar decks de escrita:", err);
    }
  };

  const fetchSavedCorrections = async () => {
    if (!user || user.isGuest) return;
    try {
      const data = await dbManager.from('user_writing_saved', user.id).select();
      if (data && Array.isArray(data)) {
        data.sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        setSavedCorrections(data);
      }
    } catch (err) {
      console.error("Fetch saved corrections error:", err);
    }
  };

  useEffect(() => {
    loadWritingDecks();
    fetchSavedCorrections();
    const onUpdate = () => { loadWritingDecks(); fetchSavedCorrections(); };
    window.addEventListener('notesDeckUpdated', onUpdate);
    return () => window.removeEventListener('notesDeckUpdated', onUpdate);
  }, [user]);

  const mapNativeLanguageToCode = (langName: string | undefined): string => {
    if (!langName) return 'pt';
    const name = langName.toLowerCase();
    if (name.includes('ingl') || name.includes('english') || name === 'en') return 'en';
    if (name.includes('fran') || name.includes('french') || name === 'fr') return 'fr';
    if (name.includes('espanh') || name.includes('spanish') || name === 'es') return 'es';
    if (name.includes('alem') || name.includes('german') || name === 'de') return 'de';
    if (name.includes('ital') || name.includes('italian') || name === 'it') return 'it';
    if (name.includes('holand') || name.includes('dutch') || name === 'nl') return 'nl';
    if (name.includes('japon') || name.includes('japanese') || name === 'ja') return 'ja';
    if (name.includes('russ') || name.includes('russian') || name === 'ru') return 'ru';
    if (name.includes('chin') || name.includes('chinese') || name === 'zh') return 'zh';
    if (name.includes('húngar') || name.includes('hungarian') || name === 'hu') return 'hu';
    if (name === 'pt') return 'pt';
    return 'pt';
  };

  const handleSaveCorrection = async (aiFeedback: string) => {
    if (!user || user.isGuest) return;
    setIsSavingCorrection(true);
    try {
      const activeCard = view === 'notesPractice' && selectedNotesDeck ? selectedNotesDeck.cards[noteCardIndex] : null;
      
      const payloadTargetLang = (view === 'notesPractice' && selectedNotesDeck)
        ? selectedNotesDeck.lang 
        : (learningLang ? learningLang.id.split('-')[0] : 'en');
        
      let originalTextToSave = activeCard?.original || selectedStory?.content || selectedStory?.texto || '';
      if (exerciseMode === 'producao') {
        if (activeCard) {
          originalTextToSave = activeCard.translation || activeCard.original;
        } else {
          originalTextToSave = translatedStoryContent || selectedStory?.nativeTranslationText || originalTextToSave;
        }
      }

      const payload = {
        id: crypto.randomUUID(),
        user_id: user.id,
        book_id: null,
        exercise_mode: exerciseMode === 'producao' ? 'criacao' : 'traducao',
        target_lang: payloadTargetLang,
        native_lang: mapNativeLanguageToCode(profile?.native_language),
        original_text: originalTextToSave,
        user_input: userInput.trim(),
        ai_correction: aiFeedback,
        created_at: getLocalISOString(),
        saved_at: getLocalISOString()
      };

      await dbManager.from('user_writing_saved', user.id).insert(payload);
      setSavedCorrectionSuccess(true);
      fetchSavedCorrections();

      const hasSeenModal = localStorage.getItem('libritui_has_seen_save_modal');
      if (!hasSeenModal) {
        setShowSavedCorrectionModal(true);
        localStorage.setItem('libritui_has_seen_save_modal', 'true');
      }
    } catch (error) {
      console.error("Error saving correction:", error);
    } finally {
      setIsSavingCorrection(false);
    }
  };

  useEffect(() => {
    fetchStories(activeLevel);
  }, [activeLevel, learningLang]);

  useEffect(() => {
    if (activeTab === 'focado') {
      const currentCode = (learningLang?.id || 'en-US').split('-')[0].toLowerCase();
      const deck = writingDecks.find(d => d.lang.toLowerCase().startsWith(currentCode)) || (writingDecks.length > 0 ? writingDecks[0] : null);
      setSelectedNotesDeck(deck);
      setNoteCardIndex(0);
      setUserInput('');
      setFeedback(null);
    }
  }, [activeTab, learningLang, writingDecks]);

  useEffect(() => {
    const handleTour = () => {
      if (tourService.isActive() && tourService.getPhase() === 'WRITING') {
        if (view === 'selection') {
          tourService.createDriver([
            {
              element: '#tour-writing-deck',
              popover: {
                title: 'Prática Escrita',
                description: 'Aqui você pratica tradução e criação de frases. Clique em um card para começar.',
                showButtons: []
              },
              onHighlighted: (el) => {
                 if (el) el.style.pointerEvents = 'auto';
              }
            }
          ]);
        } else {
          tourService.createDriver([
            {
              element: '#tour-writing-input',
              popover: {
                title: 'Sua Vez de Escrever',
                description: 'Traduza ou crie a frase neste campo. Volte ao Hub para irmos à próxima ferramenta: Voz Ativa!',
                doneBtnText: 'Próximo',
                nextBtnText: 'Próximo',
                onNextClick: () => {
                  tourService.setPhase('GOTO_VOZ_ATIVA');
                  tourService.stopTour();
                  navigate(-1);
                }
              }
            }
          ]);
        }
      }
    };

    setTimeout(handleTour, 800);
    window.addEventListener('libritui-tour-started', handleTour);
    return () => window.removeEventListener('libritui-tour-started', handleTour);
  }, [view, stories]);

  // Helper for language mapping
  const getLanguageFolder = (langCode: string) => {
    if (!langCode) return 'ingles';
    const code = langCode.toLowerCase().split('-')[0];
    switch (code) {
      case 'pt': return 'portugues';
      case 'es': return 'espanhol';
      case 'fr': return 'frances';
      case 'de': return 'alemao';
      case 'it': return 'italiano';
      case 'ja': return 'japones';
      case 'ru': return 'russo';
      case 'zh': return 'chines';
      case 'nl': return 'holandes';
      default: return 'ingles';
    }
  };

  // Translate story content if in production mode for general study
  useEffect(() => {
    if (view === 'practice' && selectedStory && exerciseMode === 'producao') {
      if (selectedStory.nativeTranslationText) {
        setTranslatedStoryContent(selectedStory.nativeTranslationText);
      } else {
        const translate = async () => {
          const native = mapNativeLanguageToCode(profile?.native_language);
          const textToTranslate = selectedStory.content || selectedStory.texto;
          if (!textToTranslate) return;
          try {
            const res = await translationService.unifiedTranslate(textToTranslate, native);
            setTranslatedStoryContent(res.translation);
          } catch (e) {
            console.error("Translation failed", e);
            setTranslatedStoryContent("Erro ao carregar a tradução.");
          }
        };
        translate();
      }
    } else {
      setTranslatedStoryContent(null);
    }
  }, [view, selectedStory, exerciseMode, user]);

  const fetchStories = async (level: string) => {
    setIsLoading(true);
    try {
      const getLanguageFileSuffix = (langCode: string): string => {
        const normalized = langCode.toLowerCase().split('-')[0];
        if (normalized === 'pt') return 'ptbr';
        return normalized;
      };

      const targetSuffix = getLanguageFileSuffix(learningLang.id);
      const nativeSuffix = getLanguageFileSuffix(profile?.native_language || 'pt');

      const targetUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/writing-lessons/writing-content-${targetSuffix}.json`;
      const nativeUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/writing-lessons/writing-content-${nativeSuffix}.json`;

      let targetData: any = null;
      let nativeData: any = null;

      try {
        const res = await fetch(targetUrl);
        if (res.ok) targetData = await res.json();
      } catch (e) {
        console.error("Error fetching target writing content:", e);
      }

      try {
        const res = await fetch(nativeUrl);
        if (res.ok) nativeData = await res.json();
      } catch (e) {
        console.error("Error fetching native writing translation:", e);
      }

      // 1. Tentar carregar histórias com áudio pré-gravado do main-checklist.json
      let checklistStories: any[] = [];
      const langKey = (learningLang?.id || 'en').split('-')[0].toLowerCase();
      const folder = WRITING_AUDIO_LANG_MAP[langKey] || WRITING_AUDIO_LANG_MAP[learningLang?.folder] || null;
      const levelKey = level.toUpperCase();

      if (folder) {
        try {
          const checklistRes = await fetch('https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/writing-audio/main-checklist.json');
          if (checklistRes.ok) {
            const checklistData = await checklistRes.json();
            if (Array.isArray(checklistData)) {
              const matched = checklistData.filter((x: any) => x.folder_name === folder && x.level === levelKey);
              const ptStories = checklistData.filter((x: any) => x.folder_name === 'portuguese' && x.level === levelKey);

              checklistStories = matched.map((item: any) => {
                const ptItem = ptStories.find((p: any) => p.id === item.id);
                const audioUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/writing-audio/${folder}/${levelKey}/${item.audio_file}`;
                return {
                  id: item.id,
                  title: item.title,
                  texto: item.content,
                  content: item.content,
                  audioUrl: audioUrl,
                  audio_file: item.audio_file,
                  nativeTranslationText: ptItem?.content || '',
                  nativeTranslationTitle: ptItem?.title || ''
                };
              });
            }
          }
        } catch (e) {
          console.warn('[WritingPage] Erro ao buscar main-checklist.json:', e);
        }
      }

      let allStories: any[] = [];
      if (checklistStories.length > 0) {
        allStories = checklistStories;
      } else {
        const targetStories = targetData?.escrita?.ingles?.[levelKey] || [];
        const nativeStories = nativeData?.escrita?.ingles?.[levelKey] || [];

        let dynamicStories = targetStories.map((story: any) => {
          const nativeStory = nativeStories.find((ns: any) => ns.id === story.id);
          return {
            ...story,
            texto: story.content || story.texto || '',
            nativeTranslationText: nativeStory?.content || nativeStory?.texto || '',
            nativeTranslationTitle: nativeStory?.title || nativeStory?.titulo || ''
          };
        });
        allStories = dynamicStories;
      }
      
      const uniqueMap = new Map();
      allStories.forEach(story => {
        const key = story.id !== undefined ? String(story.id) : (story.title || story.titulo);
        if (key && !uniqueMap.has(key)) {
          uniqueMap.set(key, story);
        }
      });
      const finalStories = Array.from(uniqueMap.values());
      setStories(finalStories);

      // Auto-selecionar história para abrir direto em prática
      if (finalStories.length > 0) {
        setSelectedStory(finalStories[0]);
        setCurrentStoryIndex(0);
        if (view !== 'notesPractice') {
          setView('practice');
        }
      }
    } catch (error) {
      console.error("Erro ao carregar histórias do R2:", error);
      setStories([]);
    } finally {
      setIsLoading(false);
    }
  };
  const PROFESSOR_MESSAGES = [
    "O Professor foi buscar um café! ☕ Ele volta em alguns segundos para avaliar sua tradução. Estamos trabalhando para estabilizar a conexão e corrigir esse erro.",
    "Muitos alunos na sala agora! 👨‍🏫 O Professor pediu um segundinho para respirar. Tente novamente em instantes. Já estamos cientes e trabalhando para corrigir esse erro.",
    "O Professor está conferindo o dicionário... 📖 A conexão falhou por um momento, tente clicar novamente! Nossa equipe está trabalhando para corrigir esse erro o mais rápido possível.",
    "O Professor deu uma escapadinha da sala, mas já volta! 🏃‍♂️ Tente corrigir sua frase novamente mais tarde. Este é um erro temporário e já estamos trabalhando na solução."
  ];

  const handleAICorrect = async (originalText?: string | any, userText?: string, mode: 'traducao' | 'producao' = 'traducao', referenceTranslation?: string) => {
    if (isCorrecting) return; // CLICK GUARD: Prevent multiple calls if already running

    // Reset feedback/error state immediately
    setFeedback(null);
    setSavedCorrectionSuccess(false);

    // If originalText is a React synthetic event, ignore it
    const actualOriginal = (typeof originalText === 'string')
      ? originalText
      : (selectedStory?.content || selectedStory?.texto || '');

    const actualUserTranslation = (typeof userText === 'string')
      ? userText
      : userInput.trim();

    if (!actualUserTranslation) return;

    setIsCorrecting(true);
    try {
      // Use targetLanguage from useTheme() to ensure we have the absolute latest interface language
      const exerciseId = selectedStory?.id || `custom_${Date.now()}`;
      const mappedNativeLang = mapNativeLanguageToCode(profile?.native_language);
      const result = await groqService.generateFeedbackViaProxy(actualOriginal, actualUserTranslation, mappedNativeLang, activeLevel, mode, referenceTranslation, exerciseId);
      setFeedback(result);

      // Record precision and consistency metrics in D1
      const payloadTargetLang = (view === 'notesPractice' && selectedNotesDeck)
        ? selectedNotesDeck.lang 
        : (learningLang ? learningLang.id.split('-')[0] : 'en');

      const scoreMatch = (typeof result === 'string' ? result : (result?.feedback || '')).match(/(\d+(?:\.\d+)?)\s*\/\s*10/);
      let numericScore = 0;
      if (scoreMatch) {
        numericScore = parseFloat(scoreMatch[1]);
      } else if (typeof result === 'object' && typeof result?.score === 'number') {
        numericScore = result.score > 10 ? result.score / 10 : result.score;
      }
      const isWritingCorrect = numericScore > 6;
      activeStudyTracker.logExercisePrecision(isWritingCorrect, user?.id, payloadTargetLang);
      activeStudyTracker.logConsistencySession(user?.id, payloadTargetLang);

      // Record writing completion in uniqueWordsService
      import('../services/uniqueWordsService').then(({ uniqueWordsService }) => {
        uniqueWordsService.recordExerciseCompletion(
          user?.id,
          payloadTargetLang,
          'writing',
          exerciseId,
          `${actualOriginal} ${actualUserTranslation}`
        );
      });

      // Record exercise progress for study plan & tracking
      exerciseProgressService.recordPractice({
        userId: user?.id,
        exerciseType: 'writing',
        langCode: payloadTargetLang,
        level: activeLevel,
        itemId: exerciseId,
        score: Math.round(numericScore * 10),
        isSuccess: isWritingCorrect
      }).catch(err => console.warn('[WritingPage] Error recording progress:', err));
      
      // EXTRACAO DA NOTA E SALVAMENTO BLINDADO DE CORRECOES <= 6
      if (scoreMatch) {
        const score = parseFloat(scoreMatch[1]);
        soundService.play(score >= 7 ? 'success' : score < 5 ? 'error' : 'partial');
        if (score <= 6) {
          const payloadTargetLang = (view === 'notesPractice' && selectedNotesDeck)
            ? selectedNotesDeck.lang 
            : (learningLang ? learningLang.id.split('-')[0] : 'en');

          if (user) {
            import('../services/errorReviewService').then(({ errorReviewService }) => {
              errorReviewService.recordError({
                userId: user.id,
                language: payloadTargetLang,
                exerciseType: 'writing',
                sourceOrigin: 'direct_url',
                itemId: `writing_${Date.now()}`,
                itemPayload: { prompt: actualOriginal, expected: referenceTranslation || '' },
                userInput: userText
              });
            });
          }
            
          
          let grammarReport = null;
          const grammarMatch = result.match(/5\.\s*[A-Za-zÀ-ÖØ-öø-ÿ]+:\s*([\s\S]*)/i);
          if (grammarMatch && grammarMatch[1]) {
            grammarReport = grammarMatch[1].trim();
          }
          
          const saveCorrection = async () => {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              const token = session?.access_token;
              
              await fetch('/api/writing-corrections', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                  id: crypto.randomUUID(),
                  user_id: user ? user.id : "NULL",
                  exercise_mode: mode === 'producao' ? 'creation' : 'translation',
                  target_lang: payloadTargetLang,
                  native_lang: mappedNativeLang,
                  original_text: actualOriginal,
                  user_input: actualUserTranslation,
                  ai_correction: result,
                  score: score,
                  grammar_report: grammarReport,
                  created_at: getLocalISOString()
                })
              });
            } catch (e) {
              console.error('[WritingPage] Low score save failed:', e);
            }
          };
          saveCorrection();
        }
      }
      
      // Increment streak on successful interaction
      if (user) {
        streakService.validateAndIncrementStreak(user.id, 'writing').then(result => {
          refreshProfile(true);
          if (result && result.newlyIncremented) {
            window.dispatchEvent(new CustomEvent('libritui-streak-updated', { detail: result }));
          }
        });

                      // STATISTICS: Increment writing exercises
                      statsService.increment('words_written', user.id, 1, { langCode: learningLang?.id?.split('-')[0] || 'all' });
      }
      
      if (!isPro) setCorrectionCount(prev => prev + 1);

    } catch (err: any) {
      console.error("Gemini Proxy Error:", err);
      if (err.message === "Limite de 5 mensagens atingido." || (err.message && err.message.includes("Limite de 5 mensagens atingido"))) {
        setShowLimitModal(true);
        setCorrectionCount(FREE_LIMIT);
        setFeedback(null);
      } else {
        const randomMessage = PROFESSOR_MESSAGES[Math.floor(Math.random() * PROFESSOR_MESSAGES.length)];
        setFeedback(randomMessage);
      }
    } finally {
      setIsCorrecting(false);
    }
  };


  const totalPages = Math.ceil(stories.length / 6);
  const currentStories = stories.slice((currentPage - 1) * 6, currentPage * 6);

  const levelColorMap: Record<string, string> = {
    'A1': 'bg-tintd-lavender', 'A2': 'bg-tintd-sky', 'B1': 'bg-ok',
    'B2': 'bg-warn', 'C1': 'bg-warn', 'C2': 'bg-bad'
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 max-w-5xl mx-auto py-6 px-4 pb-24">
      {/* Top Header */}
      <div className="space-y-5 pb-2">
        <AppBackButton onClick={handleGoBack} />
        <StudyPageHeader tourKey="writing" title={tx('short_title', 'Escrita')} />
      </div>

      {/* Selectors Bar (4 Dropdowns: Idioma, Dificuldade, Modo de Prática, Estudo) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Language Selector */}
        <div data-tour="writing-language" className="bg-slate-50 dark:bg-slate-900 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 relative">
          <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2 block flex items-center gap-2">
            <Globe size={14} className="text-primary" />
            Selecione o Idioma
          </label>
          <div className="relative">
            <select
              value={learningLang.id}
              onChange={(e) => {
                const selected = LANGUAGES.find(l => l.id === e.target.value);
                if (selected) handleLanguageChange(selected);
              }}
              className="w-full appearance-none bg-transparent outline-none pr-10 font-bold text-ink text-sm cursor-pointer"
            >
              {LANGUAGES.map(l => (
                <option key={l.id} value={l.id} className="bg-surface text-ink">
                  {l.flag} {l.name}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Difficulty / Level Selector */}
        <div data-tour="writing-level" className="bg-slate-50 dark:bg-slate-900 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 relative">
          <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2 block flex items-center gap-2">
            <Sparkles size={14} className="text-primary" />
            Selecione a Dificuldade
          </label>
          <div className="relative">
            <select
              value={activeLevel}
              onChange={(e) => handleLevelChange(e.target.value)}
              className="w-full appearance-none bg-transparent outline-none pr-10 font-bold text-ink text-sm cursor-pointer"
            >
              {LEVELS.map(level => (
                <option key={level} value={level} className="bg-surface text-ink">
                  {level} - {level === 'A1' ? 'Iniciante' : level === 'A2' ? 'Básico' : level === 'B1' ? 'Intermediário' : level === 'B2' ? 'Independente' : level === 'C1' ? 'Avançado' : 'Fluente'}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Practice Mode (Tradução vs Ativo) */}
        <div data-tour="writing-mode" className="bg-slate-50 dark:bg-slate-900 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 relative">
          <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2 block flex items-center gap-2">
            <Edit3 size={14} className="text-primary" />
            Modo de Prática
          </label>
          <div className="relative">
            <select
              value={exerciseMode}
              onChange={(e) => handleModeChange(e.target.value as 'traducao' | 'producao')}
              className="w-full appearance-none bg-transparent outline-none pr-10 font-bold text-ink text-sm cursor-pointer"
            >
              <option value="traducao" className="bg-surface text-ink"><T k="ui.writing.translation" /></option>
              <option value="producao" className="bg-surface text-ink">✍️ Ativo</option>
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Study Focus (Estudo Geral vs Estudo Focado) */}
        <div data-tour="writing-focus" className="bg-slate-50 dark:bg-slate-900 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl p-4 relative">
          <label className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2 block flex items-center gap-2">
            <Brain size={14} className="text-primary" />
            Estudo
          </label>
          <div className="relative">
            <select
              value={activeTab}
              onChange={(e) => {
                const mode = e.target.value as 'geral' | 'focado';
                setActiveTab(mode);
                setUserInput('');
                setFeedback(null);
              }}
              className="w-full appearance-none bg-transparent outline-none pr-10 font-bold text-ink text-sm cursor-pointer"
            >
              <option value="geral" className="bg-surface text-ink"><T k="ui.writing.general" /></option>
              <option value="focado" className="bg-surface text-ink"><T k="ui.study.focusedMyNotes" /></option>
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4 bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] my-6">
          <Loader2 className="animate-spin text-primary" size={56} />
          <p className="text-slate-500 font-normal text-xs uppercase font-mono tracking-[0.14em] animate-pulse">
            Sincronizando material de escrita...
          </p>
        </div>
      ) : activeTab === 'focado' ? (
        !selectedNotesDeck || selectedNotesDeck.cards.length === 0 ? (
          <div className="p-16 text-center bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] space-y-4 my-6">
            <div className="w-16 h-16 bg-warn/10 rounded-2xl flex items-center justify-center text-warn mx-auto">
              <Star size={32} />
            </div>
            <h3 className="text-xl font-medium text-ink tracking-[-0.02em]"><T k="ui.study.noNotes" /></h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Salve palavras ou frases com o ícone 🧠 durante leituras ou no dicionário para praticar sua escrita de forma personalizada aqui.
            </p>
          </div>
        ) : (
          (() => {
            const noteCard = selectedNotesDeck.cards[noteCardIndex];
            return (
              <div className="space-y-6">
                <div className="flex items-center justify-between text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 px-2">
                  <span>Nota {noteCardIndex + 1} de {selectedNotesDeck.cards.length} ({selectedNotesDeck.name})</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setNoteCardIndex(i => Math.max(0, i - 1)); setUserInput(''); setFeedback(null); }}
                      disabled={noteCardIndex === 0}
                      className="p-1.5 rounded-lg border border-black/[0.06] dark:border-white/[0.06] text-slate-500 hover:text-primary disabled:opacity-30 disabled:hover:text-slate-500 transition-colors"
                      title="Nota anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => { setNoteCardIndex(i => Math.min(selectedNotesDeck.cards.length - 1, i + 1)); setUserInput(''); setFeedback(null); }}
                      disabled={noteCardIndex >= selectedNotesDeck.cards.length - 1}
                      className="p-1.5 rounded-lg border border-black/[0.06] dark:border-white/[0.06] text-slate-500 hover:text-primary disabled:opacity-30 disabled:hover:text-slate-500 transition-colors"
                      title={i18nInstance.t('ui.writing.nextNote')}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div className="space-y-6">
                    <div className="bg-surface rounded-[28px] p-8 border border-black/[0.06] dark:border-white/[0.06] space-y-4">
                      <h2 className="text-[10px] font-normal uppercase font-mono tracking-[0.16em] text-ink/60">
                        {exerciseMode === 'traducao' ? tx('original_phrase', 'Frase Original') : tx('translate_this', 'Traduza esta frase')}
                      </h2>
                      <p className="text-2xl font-medium dark:text-ink leading-tight">
                        "{exerciseMode === 'traducao' ? noteCard?.original : (noteCard?.translation || 'Sem tradução')}"
                      </p>
                    </div>
                    <textarea
                      id="tour-writing-input"
                      value={userInput}
                      onChange={e => setUserInput(e.target.value)}
                      placeholder={exerciseMode === 'traducao' ? "Digite sua resposta aqui..." : "Escreva o exercício aqui..."}
                      className="w-full h-44 bg-surface rounded-[28px] p-6 border border-black/[0.06] dark:border-white/[0.06] focus:border-ink/25 outline-none text-lg text-ink placeholder:text-muted resize-none transition-colors"
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleAICorrect(
                          noteCard?.original,
                          userInput.trim(),
                          exerciseMode,
                          noteCard?.translation
                        )}
                        disabled={isCorrecting || !userInput.trim() || (!isPro && correctionCount >= FREE_LIMIT)}
                        className="group flex-1 min-h-[56px] py-3 px-6 bg-primary text-slate-900 rounded-full text-sm font-medium hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40 flex flex-col items-center justify-center gap-0.5"
                      >
                        <div className="flex items-center justify-center gap-2">
                          {isCorrecting ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                          {isCorrecting ? tx('prof_reading', 'O Professor está lendo sua resposta...') : tx('correct_ai', 'Corrigir com IA')}
                        </div>
                        {!isPro && (
                          <span className="text-[10px] text-surface/60 font-mono tracking-[0.06em]">Correções gratuitas: {correctionCount}/{FREE_LIMIT}</span>
                        )}
                      </button>
                      {noteCardIndex < selectedNotesDeck.cards.length - 1 ? (
                        <button
                          onClick={() => { setNoteCardIndex(i => i + 1); setUserInput(''); setFeedback(null); }}
                          className="px-6 py-4 bg-slate-100 dark:bg-slate-800 rounded-2xl font-medium text-[11px] text-slate-500 hover:bg-slate-200 transition-all flex items-center gap-1"
                        >
                          <span>{tx('next', 'Próximo')}</span>
                          <ChevronRight size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={async () => {
                            if (user) {
                              const result = await streakService.validateAndIncrementStreak(user.id, 'writing');
                              refreshProfile(true);
                              if (result && result.newlyIncremented) {
                                window.dispatchEvent(new CustomEvent('libritui-streak-updated', { detail: result }));
                              }
                              statsService.increment('words_written', user.id, 1, { langCode: learningLang?.id?.split('-')[0] || 'all' });
                            }
                            setFeedback(null);
                            setUserInput('');
                            setNoteCardIndex(0);
                          }}
                          className="px-6 py-4 bg-ok text-surface rounded-2xl font-medium text-[11px] hover:bg-ok active:scale-95 transition-all"
                        >
                          {tx('finish', 'Finalizar')}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-6">
                    {!feedback ? (
                      <div className="h-full min-h-[260px] border border-dashed border-ink/15 rounded-[32px] flex flex-col items-center justify-center p-12 text-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-tint-butter text-ink flex items-center justify-center"><AnimatedIcon motion="bounce" active><Trophy size={24} strokeWidth={1.6} /></AnimatedIcon></div>
                        <p className="text-slate-500 font-bold">{tx('waiting_answer', 'Aguardando sua resposta...')}</p>
                      </div>
                    ) : (
                      <div className="bg-surface rounded-[28px] p-8 border border-ok/20 space-y-6 animate-in slide-in-from-right-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="text-ok" size={24} />
                            <h2 className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-ok">{tx('expert', 'Correção Especialista')}</h2>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleSaveCorrection(feedback)}
                              disabled={isSavingCorrection || savedCorrectionSuccess}
                              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400 disabled:opacity-50"
                              title={tx('save_correction', 'Salvar Correção')}
                            >
                              {isSavingCorrection ? (
                                <Loader2 size={16} className="animate-spin text-ok" />
                              ) : savedCorrectionSuccess ? (
                                <Bookmark size={16} className="fill-ok text-ok" />
                              ) : (
                                <Bookmark size={16} />
                              )}
                            </button>
                            <button onClick={() => setFeedback(null)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400" title={tx('try_again', 'Tentar novamente')}><RotateCcw size={16} /></button>
                          </div>
                        </div>
                        <div className="prose dark:prose-invert prose-sm max-w-none text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-medium leading-relaxed">{feedback}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()
        )
      ) : stories.length === 0 ? (
        <div className="p-24 text-center bg-surface rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] space-y-4 my-6">
          <Edit3 size={48} className="text-slate-300 mx-auto mb-2" />
          <h3 className="text-xl font-medium text-ink tracking-[-0.02em]"><T k="ui.writing.noText" /></h3>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            Escolha outro nível ou idioma nos seletores acima para praticar sua escrita.
          </p>
        </div>
      ) : selectedStory ? (
        <div className="space-y-6">
          {/* Top story navigation */}
          <div className="flex items-center justify-between text-xs font-normal uppercase font-mono tracking-[0.14em] text-slate-500 dark:text-slate-400 px-2">
            <span className="truncate max-w-[70%]">
              História {currentStoryIndex + 1} de {stories.length}: {selectedStory.title || selectedStory.titulo}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevStory}
                disabled={currentStoryIndex === 0}
                className="p-1.5 rounded-lg border border-black/[0.06] dark:border-white/[0.06] text-slate-500 hover:text-primary disabled:opacity-30 disabled:hover:text-slate-500 transition-colors"
                title={i18nInstance.t('ui.writing.prevStory')}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={handleNextStory}
                disabled={currentStoryIndex >= stories.length - 1}
                className="p-1.5 rounded-lg border border-black/[0.06] dark:border-white/[0.06] text-slate-500 hover:text-primary disabled:opacity-30 disabled:hover:text-slate-500 transition-colors"
                title={i18nInstance.t('ui.writing.nextStory')}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="space-y-6">
              <div className="relative writing-container bg-tint-lavender text-ink rounded-[32px] p-8 sm:p-9 space-y-5">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleToggleStoryAudio}
                    disabled={isStoryAudioLoading}
                    className={`w-10 h-10 rounded-full transition-all duration-200 flex items-center justify-center shrink-0 ${
                      isStoryAudioPlaying
                        ? 'bg-ink text-surface'
                        : 'bg-ink/10 text-ink hover:bg-ink/15 active:scale-95'
                    }`}
                    title={isStoryAudioPlaying ? "Parar áudio" : "Ouvir áudio da história"}
                    aria-label={i18nInstance.t('ui.writing.playStory')}
                  >
                    {isStoryAudioLoading ? (
                      <Loader2 size={17} className="animate-spin" />
                    ) : isStoryAudioPlaying ? (
                      <Square size={15} className="fill-current" />
                    ) : (
                      <AnimatedIcon motion="pulse"><Volume2 size={17} strokeWidth={1.75} /></AnimatedIcon>
                    )}
                  </button>
                  <h2 className="text-[10px] font-normal uppercase font-mono tracking-[0.16em] text-ink/60">
                    {exerciseMode === 'traducao' ? tx('original_text', 'Texto Original') : tx('translate_this', 'Traduza esta frase')}
                  </h2>
                </div>
                <h1 className="font-display text-3xl sm:text-[34px] font-light tracking-[-0.04em] text-ink leading-tight">
                  {exerciseMode === 'producao' ? (selectedStory.nativeTranslationTitle || selectedStory.title || selectedStory.titulo) : (selectedStory.title || selectedStory.titulo)}
                </h1>
                <p className="text-lg leading-relaxed text-ink/80 border-l-2 border-ink/20 pl-4">
                  "{exerciseMode === 'traducao' ? renderClickableText(selectedStory.content || selectedStory.texto) : renderClickableText(translatedStoryContent || selectedStory.nativeTranslationText || 'Traduzindo...', 'pt')}"
                </p>


              </div>

              <textarea
                id="tour-writing-input"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder={exerciseMode === 'traducao' ? tx('placeholder', 'Escreva sua tradução aqui...') : tx('placeholder_creation', 'Escreva o texto original aqui...')}
                className="w-full h-44 bg-surface rounded-[28px] p-6 border border-black/[0.06] dark:border-white/[0.06] focus:border-ink/25 outline-none text-lg text-ink placeholder:text-muted resize-none transition-colors"
              />

              <div className="flex gap-3">
                <button
                  onClick={() => handleAICorrect(
                    selectedStory.content || selectedStory.texto,
                    userInput.trim(),
                    exerciseMode,
                    translatedStoryContent || undefined
                  )}
                  disabled={isCorrecting || !userInput.trim() || (!isPro && correctionCount >= FREE_LIMIT)}
                  className="group flex-1 min-h-[56px] py-3 px-6 bg-primary text-slate-900 rounded-full text-sm font-medium hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40 flex flex-col items-center justify-center gap-0.5"
                >
                  <div className="flex items-center justify-center gap-3">
                    {isCorrecting ? <Loader2 className="animate-spin" size={17} /> : <AnimatedIcon motion="pop"><Sparkles size={17} strokeWidth={1.75} /></AnimatedIcon>}
                    {isCorrecting ? tx('prof_reading', 'O Professor está lendo sua resposta...') : tx('correct_btn', 'Corrigir com Professor LibriTui')}
                  </div>
                  {!isPro && (
                    <span className="text-[10px] text-surface/60 font-mono tracking-[0.06em]">Correções gratuitas diárias: {correctionCount}/{FREE_LIMIT}</span>
                  )}
                </button>
                {currentStoryIndex < stories.length - 1 && (
                  <button
                    onClick={handleNextStory}
                    className="group min-h-[56px] pl-6 pr-4 bg-transparent border border-ink/15 rounded-full text-sm font-medium text-ink hover:bg-ink/5 transition-all flex items-center gap-2"
                    title={i18nInstance.t('ui.writing.nextStory')}
                  >
                    <span>{tx('next', 'Próxima')}</span>
                    <ChevronRight size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-6">
              {!feedback ? (
                <div className="h-full min-h-[260px] border border-dashed border-ink/15 rounded-[32px] flex flex-col items-center justify-center p-12 text-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-tint-butter text-ink flex items-center justify-center"><AnimatedIcon motion="bounce" active><Trophy size={24} strokeWidth={1.6} /></AnimatedIcon></div>
                  <p className="text-muted text-sm max-w-[240px]">{tx('waiting_placeholder', 'Aguardando sua tradução para avaliação...')}</p>
                </div>
              ) : (
                <div className="bg-surface rounded-[28px] p-8 border border-ok/20 space-y-6 animate-in slide-in-from-right-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="text-ok" size={24} />
                      <h2 className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-ok">{tx('expert', 'Correção Especialista')}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSaveCorrection(feedback)}
                        disabled={isSavingCorrection || savedCorrectionSuccess}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400 disabled:opacity-50"
                        title={tx('save_correction', 'Salvar Correção')}
                      >
                        {isSavingCorrection ? (
                          <Loader2 size={16} className="animate-spin text-ok" />
                        ) : savedCorrectionSuccess ? (
                          <Bookmark size={16} className="fill-ok text-ok" />
                        ) : (
                          <Bookmark size={16} />
                        )}
                      </button>
                      <button onClick={() => setFeedback(null)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400" title={tx('try_again', 'Tentar novamente')}><RotateCcw size={16} /></button>
                    </div>
                  </div>
                  <div className="prose dark:prose-invert prose-sm max-w-none text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-medium leading-relaxed">
                    {feedback}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}


      {/* Mode Tour Modal */}
      <AnimatePresence>
        {showModeTour && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
              onClick={() => setShowModeTour(false)} 
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="relative bg-surface rounded-[28px] p-8 max-w-md w-full"
            >
              <div className="space-y-6 text-center">
                <div className="flex justify-center gap-4">
                  <div className="w-14 h-14 bg-tint-sage rounded-full flex items-center justify-center text-primary">
                    <Globe size={32} />
                  </div>
                  <div className="w-16 h-16 bg-ok/10 rounded-2xl flex items-center justify-center text-ok">
                    <Edit3 size={32} />
                  </div>
                </div>
                <h3 className="text-2xl font-medium dark:text-ink">{tx('writing.modes_title', 'Modos de Prática')}</h3>
                <p className="text-slate-500 font-medium text-sm text-left">
                  <strong className="text-primary block mb-1">{tx('writing.mode_translation_title', 'Modo Tradução')}:</strong> 
                  {tx('writing.mode_translation_desc', 'Você lerá a frase no idioma que está estudando e deverá escrevê-la no seu idioma nativo. Foco em compreensão!')}<br/><br/>
                  <strong className="text-ok block mb-1">{tx('writing.mode_active_title', 'Modo Ativo')}:</strong> 
                  {tx('writing.mode_active_desc', 'Você lerá a frase no seu idioma nativo e deverá construí-la no idioma que está estudando. Foco em produção!')}
                </p>
                <div className="pt-4">
                  <button onClick={() => setShowModeTour(false)} className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 transition-all">{tx('writing.understood', 'Entendi')}</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Study Type Tour Modal */}
      <AnimatePresence>
        {showTypeTour && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
              onClick={() => setShowTypeTour(false)} 
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="relative bg-surface rounded-[28px] p-8 max-w-md w-full"
            >
              <div className="space-y-6 text-center">
                <div className="flex justify-center gap-4">
                  <div className="w-14 h-14 bg-tint-sage rounded-full flex items-center justify-center text-primary">
                    <Brain size={32} />
                  </div>
                  <div className="w-16 h-16 bg-warn/10 rounded-2xl flex items-center justify-center text-warn">
                    <Star size={32} />
                  </div>
                </div>
                <h3 className="text-2xl font-medium dark:text-ink">{tx('writing.study_types_title', 'Tipos de Estudo')}</h3>
                <p className="text-slate-500 font-medium text-sm text-left">
                  <strong className="text-primary block mb-1">{tx('writing.study_general_title', 'Estudo Geral')}:</strong> 
                  {tx('writing.study_general_desc', 'Explore textos selecionados pela LibriTui, divididos por nível de dificuldade, ideais para praticar temas variados.')}<br/><br/>
                  <strong className="text-warn block mb-1">{tx('writing.study_focused_title', 'Estudo Focado')}:</strong> 
                  {tx('writing.study_focused_desc', 'Pratique com as anotações e frases que você salvou durante a leitura dos seus livros. É o seu deck pessoal de vocabulário!')}
                </p>
                <div className="pt-4">
                  <button onClick={() => setShowTypeTour(false)} className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 transition-all">{tx('writing.understood', 'Entendi')}</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de Limite Free */}
      <AnimatePresence>
        {showLimitModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={() => setShowLimitModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-8 max-w-sm w-full overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-warn via-warn to-bad" />
              
              <div className="flex flex-col items-center text-center space-y-6">
                <div className="w-16 h-16 bg-warn/10 rounded-full flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-warn" />
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold text-white">
                    Limite atingido
                  </h3>
                  <p className="text-sm font-medium text-slate-400">
                    Você atingiu o limite de correções diárias. Faça upgrade para o LibriTui Premium para correções ilimitadas com o Professor!
                  </p>
                </div>

                <div className="w-full space-y-3 pt-2">
                  <button 
                    onClick={() => navigate('/libritui-pro')}
                    className="w-full py-4 bg-primary hover:bg-primary/90 text-slate-900 rounded-[1.5rem] font-semibold text-sm transition-all hover:scale-[1.02] active:scale-95"
                  >
                    Ver planos Premium
                  </button>
                  <button 
                    onClick={() => setShowLimitModal(false)}
                    className="w-full py-4 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-[1.5rem] font-semibold text-sm transition-all"
                  >
                    Voltar
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WritingPage;
